import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import moment from "moment";
import "moment/locale/tr";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  BulkSnoozePreviewResponse,
  PaymentPlanSummary,
  previewSnooze,
} from "../../Services/SnoozeService";
import { getRemoteText, getRemoteValue } from "../../Utils/firebase";
import { FormatAmountSymbol } from "../FormatData/FormatAmount";

export interface BulkSnoozePaymentSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  /** `YYYY-MM-DD` from the date modal — same as `previewSnooze` request. */
  snoozeDate: string;
  paymentPlans: PaymentPlanSummary[];
  /** Latest `totalSnoozeFee` from `previewSnooze` (authoritative when present). */
  serviceFeeFromPreview?: number;
  /** When set (e.g. reopening from summary edit), restores this selection among eligible plans. */
  initialSelectedPaymentPlanIds?: number[];
  /** After `previewSnooze` with **selected IDs only**; parent keeps full list from first response. */
  onPreviewUpdated?: (
    preview: BulkSnoozePreviewResponse,
    selectedPaymentPlanIds: number[]
  ) => void;
  /** Primary action: e.g. continue to summary or pay (label from remote / default). */
  onPayPressed: (selectedPaymentPlanIds: number[]) => void;
}

const BORDER_GRAY = "#E2E8F0";
const TEXT_PRIMARY = "#191B1E";
const TEXT_MUTED = "#64748B";
const GREEN_SNOOZE = "#16A34A";

function toNum(v: string | number | undefined): number {
  if (v === undefined || v === null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const n = parseFloat(String(v).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function PlanCheckboxIcon({ checked }: { checked: boolean }) {
  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        border: checked ? `5px solid ${TEXT_PRIMARY}` : `2.5px solid ${BORDER_GRAY}`,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >

    </Box>
  );
}

const BulkSnoozePaymentSelectionModal = ({
  open,
  onClose,
  onBack,
  snoozeDate,
  paymentPlans,
  serviceFeeFromPreview,
  initialSelectedPaymentPlanIds,
  onPreviewUpdated,
  onPayPressed,
}: BulkSnoozePaymentSelectionModalProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [agreed, setAgreed] = useState(false);
  const prevOpenRef = useRef(false);

  const eligiblePlans = useMemo(
    () => paymentPlans.filter((p) => p.snoozeAvailable),
    [paymentPlans]
  );
  const ineligiblePlans = useMemo(
    () => paymentPlans.filter((p) => !p.snoozeAvailable),
    [paymentPlans]
  );

  /** Only when the dialog opens — not when parent refreshes preview totals. */
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      const eligibleIds = paymentPlans
        .filter((p) => p.snoozeAvailable)
        .map((p) => p.paymentPlanId);
      const fromInitial =
        initialSelectedPaymentPlanIds?.filter((id) => eligibleIds.includes(id)) ?? [];
      const fromSelectedFlags = paymentPlans
        .filter((p) => p.snoozeAvailable && p.selected)
        .map((p) => p.paymentPlanId);
      const next =
        fromInitial.length > 0
          ? fromInitial
          : fromSelectedFlags.length > 0
            ? fromSelectedFlags
            : eligibleIds;
      setSelectedIds(new Set(next));
      setAgreed(false);
    }
    prevOpenRef.current = open;
  }, [open, paymentPlans, initialSelectedPaymentPlanIds]);

  const title =
    getRemoteValue("bulk_snooze_payment_selection_title") ||
    "Ertelenecek Ödemeleri Seç";
  const sectionEligible =
    getRemoteValue("bulk_snooze_payment_selection_eligible") ||
    "Ertelenmeye Uygun";
  const sectionIneligible =
    getRemoteValue("bulk_snooze_payment_selection_ineligible") ||
    "Ertelenmeye Uygun Değil";
  const transactionWord =
    getRemoteValue("bulk_snooze_payment_transaction") || "işlem";
  const labelServiceFee =
    getRemoteValue("bulk_snooze_summary_service_fee") || "Erteleme Hizmet Bedeli";
  const agreementPrefix =
    getRemoteValue("bulk_snooze_agreement_prefix") || "Colendi ";
  const agreementLinkText =
    getRemoteValue("bulk_snooze_agreement_link") || "Erteleme Sözleşmesi'ni";
  const agreementSuffix =
    getRemoteValue("bulk_snooze_agreement_suffix") || " okudum, onaylıyorum.";
  const ineligibleHint =
    getRemoteValue("bulk_snooze_payment_selection_ineligible_hint") ||
    "Harcamanızı, son ödeme tarihinden maksimum 90 güne kadar erteleyebilirsiniz.";

  const selectedFeeTotal = useMemo(() => {
    let sum = 0;
    eligiblePlans.forEach((p) => {
      if (selectedIds.has(p.paymentPlanId)) {
        sum += toNum(p.snoozeFee);
      }
    });
    return sum;
  }, [eligiblePlans, selectedIds]);

  const displayServiceFee = useMemo(() => {
    return selectedFeeTotal;
  }, [selectedFeeTotal]);

  // CRL-3241 (new-design.html): pay button carries the (live) fee amount
  const payButtonLabel = getRemoteText(
    "snooze_pay_and_defer_x",
    "{val} öde ve ertele",
    FormatAmountSymbol(displayServiceFee)
  );

  const togglePlan = (plan: PaymentPlanSummary) => {
    if (!plan.snoozeAvailable) return;

    let nextPaymentPlanIds: number[] = [];

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(plan.paymentPlanId)) {
        next.delete(plan.paymentPlanId);
      } else {
        next.add(plan.paymentPlanId);
      }
      nextPaymentPlanIds = Array.from(next);
      return next;
    });

    if (!snoozeDate.trim()) return;

    void (async () => {
      const preview = await previewSnooze({
        snoozeDate,
        paymentPlanIds: nextPaymentPlanIds,
      });
      if (preview) {
        onPreviewUpdated?.(preview, nextPaymentPlanIds);
      }
    })();
  };

  const handleClose = () => {
    setAgreed(false);
    onClose();
  };

  const handlePrimary = () => {
    const ids = eligiblePlans
      .filter((p) => selectedIds.has(p.paymentPlanId))
      .map((p) => p.paymentPlanId);
    if (ids.length === 0 || !agreed) return;
    onPayPressed(ids);
  };

  const canSubmit =
    agreed && eligiblePlans.some((p) => selectedIds.has(p.paymentPlanId));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: "24px",
          m: 2,
          maxWidth: "575px",
          width: "100%",
          height: "100%",
          maxHeight: { xs: "min(85vh, 1080px)", md: "min(70vh, 720px)" },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Stack direction="column" spacing="2.5px" sx={{ flexShrink: 0 }}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 4,
            top: 4,
            zIndex: 1,
            color: (theme) => theme.palette.grey[700],
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            position: "relative",
            pt: 3,
            pb: 1,
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton
            aria-label="back"
            onClick={onBack}
            sx={{
              position: "absolute",
              left: 4,
              color: TEXT_PRIMARY,
            }}
          >
            <ArrowBackOutlinedIcon sx={{ fontSize: 22 }} />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: { xs: "16px", md: "20px" },
              color: TEXT_PRIMARY,
              px: 6,
              textAlign: "center",
            }}
          >
            {title}
          </Typography>
        </Box>
      </Stack>

      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 2.5,
          pt: 2,
          pb: 2,
        }}
      >
        <Stack spacing={0} sx={{ maxWidth: 450, mx: "auto" }}>
          <SectionHeader
            label={sectionEligible}
            count={eligiblePlans.length}
            transactionWord={transactionWord}
          />
          {eligiblePlans.map((plan, idx) => (
            <Box key={plan.paymentPlanId}>
              {idx > 0 && (
                <Box sx={{ borderTop: `1px solid ${BORDER_GRAY}`, my: 0 }} />
              )}
              <PlanRow
                plan={plan}
                selected={selectedIds.has(plan.paymentPlanId)}
                onToggle={() => togglePlan(plan)}
                disabled={false}
              />
            </Box>
          ))}

          {ineligiblePlans.length > 0 && (
            <>
              <Box sx={{ height: 16 }} />
              <SectionHeader
                label={sectionIneligible}
                count={ineligiblePlans.length}
                transactionWord={transactionWord}
              />
              {ineligiblePlans.map((plan, idx) => (
                <Box key={plan.paymentPlanId}>
                  {idx > 0 && (
                    <Box sx={{ borderTop: `1px solid ${BORDER_GRAY}` }} />
                  )}
                  <PlanRow
                    plan={plan}
                    selected={false}
                    onToggle={() => { }}
                    disabled
                  />
                </Box>
              ))}
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{ pt: 2, pb: 1 }}
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 20, color: "#FF3030", mt: 0.25, flexShrink: 0 }}
                />
                <Typography sx={{ fontSize: "12px", lineHeight: 1.4 }}>
                  {ineligibleHint}
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>

      <Box
        sx={{
          flexShrink: 0,
          px: 2.5,
          pt: 2,
          pb: 3,
        }}
      >
        <Stack sx={{ maxWidth: 480, mx: "auto" }} spacing={0}>
          <Box
            sx={{
              mb: 1.5,
            }}
            className="justify-self-center"
          >
            <Stack sx={{
              borderTop: `1px solid ${BORDER_GRAY}`,
              borderBottom: `1px solid ${BORDER_GRAY}`,
              py: 2,
            }} direction="row" alignItems="center" justifyContent="space-between" className="max-w-[450px] mx-auto">
              <Typography sx={{ fontSize: { xs: "14px", md: "15px" }, color: TEXT_MUTED }}>
                {labelServiceFee}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "28px", md: "36px" },
                  fontWeight: 700,
                  color: TEXT_PRIMARY,
                  lineHeight: 1.1,
                }}
              >
                {FormatAmountSymbol(displayServiceFee)}
              </Typography>
            </Stack>
          </Box>
          <div className="h-[14px]" />
          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={(_, v) => setAgreed(v)}
                sx={{
                  color: BORDER_GRAY,
                  p: 0,
                  pr: 1,
                  "&.Mui-checked": { color: "#191B1E" },
                }}
              />
            }
            label={
              <Typography component="span" sx={{ fontSize: { xs: "13px", md: "15px" }, color: TEXT_PRIMARY }}>
                {agreementPrefix}
                <Link
                  href={"https://documents.colendilabs.com/credit-docs/moneypay-agreements/Dijital-Alisveris-Kodu-Erteleme-Hizmeti-Onay-Sozlesmesi-03-10-2024.html"}
                  underline="always"
                  target="_blank"
                  sx={{ color: TEXT_PRIMARY, fontWeight: 500 }}
                >
                  {agreementLinkText}
                </Link>
                {agreementSuffix}
              </Typography>
            }
            sx={{ alignItems: "center", ml: 0, mr: 0, mb: 1.75 }}
          />
          <div className="h-[8px]" />

          <Button
            fullWidth
            variant="contained"
            disabled={!canSubmit}
            onClick={handlePrimary}
            sx={{
              py: 1.35,
              borderRadius: "15px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "15px", md: "18px" },
              boxShadow: "none",
              bgcolor: canSubmit ? "#191B1E" : "#CBD5E1",
              color: "#fff",
              "&:hover": {
                boxShadow: "none",
                bgcolor: canSubmit ? "#1E293B" : "#CBD5E1",
              },
              "&.Mui-disabled": {
                bgcolor: "#CBD5E1",
                color: "#fff",
              },
            }}
          >
            {payButtonLabel}
          </Button>
        </Stack>
      </Box >
    </Dialog >
  );
};

function SectionHeader({
  label,
  count,
  transactionWord,
}: {
  label: string;
  count: number;
  transactionWord: string;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ pb: 1.5, pt: 0.5 }}
    >
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: TEXT_PRIMARY }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.8rem", color: TEXT_MUTED }}>
        {count} {transactionWord}
      </Typography>
    </Stack>
  );
}

function PlanRow({
  plan,
  selected,
  onToggle,
  disabled,
}: {
  plan: PaymentPlanSummary;
  selected: boolean;
  onToggle: () => void;
  disabled: boolean;
}) {
  const dueDateObj = moment(plan.dueDate).locale("tr");
  const isLate = dueDateObj.isBefore(moment());

  const amount = toNum(plan.snoozableAmount);
  const fee = toNum(plan.snoozeFee);
  const dueLabel = plan.dueDate
    ? dueDateObj.format("DD.MM.YYYY")
    : "—";
  const diffDays = plan.diffDays ?? 0;
  const snoozeLine =
    diffDays > 0
      ? `+${diffDays} gün ertelenecek`
      : getRemoteValue("bulk_snooze_payment_selection_snooze_line") || "Ertelenecek";

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      onClick={disabled ? undefined : onToggle}
      sx={{
        py: 1.75,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.45 : 1,
        userSelect: "none",
      }}
    >
      {!disabled ? (
        <Box sx={{ mr: 0.5, flexShrink: 0, display: "flex", alignItems: "center" }}>
          <PlanCheckboxIcon checked={selected} />
        </Box>
      ) : (
        <Box sx={{ width: 22, height: 22, flexShrink: 0, mr: 0.5 }} />
      )}

      <Avatar
        variant="rounded"
        src={plan.shopLogoUrl || undefined}
        alt=""
        sx={{ width: 44, height: 44, borderRadius: "10px", flexShrink: 0 }}
      />

      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: { xs: "16px", md: "17px" }, color: TEXT_PRIMARY }}>
          {plan.shopName}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {isLate && <img src="/icons/triangle_exclamation.svg" alt="late" style={{ width: "12px", height: "12px" }} />}
          <Typography sx={{ fontSize: { xs: "12px", md: "14px" }, color: isLate ? "#FF3030" : TEXT_MUTED }}>
            {
              (!disabled) ? <>
                {FormatAmountSymbol(amount)} - {dueLabel}
              </> : <>{dueLabel}</>
            }
          </Typography>
        </Stack>
        {!disabled &&
          <Typography sx={{ fontSize: "12px", fontWeight: 500, color: GREEN_SNOOZE }}>
            {snoozeLine}
          </Typography>
        }
      </Stack>
      {!disabled &&
        <Typography
          sx={{
            fontSize: { xs: "16px", md: "17px" },
            fontWeight: 700,
            color: TEXT_PRIMARY,
            flexShrink: 0,
          }}
        >
          {FormatAmountSymbol(fee)}
        </Typography>
      }
    </Stack>
  );
}

export default BulkSnoozePaymentSelectionModal;
